import { FC } from 'react';
import { useFlag } from '@reflag/react-sdk';
import Typography from '@mui/material/Typography';
import { grey } from '@mui/material/colors';
import { Column, Row } from 'further-ui/components/Layout';
import { type Column as TableColumn } from 'further-ui/components/Table';
import { CalendarDay, numberToCurrencyString } from 'further-ui/utils';
import { useFilters } from 'further-ui/hooks';
import { AccountingReportDateOption } from 'further-types/lp/general-ledger';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useLayoutContext } from 'contexts/LayoutContext';
import { SyncScrollProvider } from 'contexts/SyncScrollContext';
import PageLoader from 'components/PageLoader';
import PageFilters from 'components/new/PageFilters';
import downloadXlxs from 'utils/downloadXlxs';
import api from 'lib/trpcClient';
import AccountingTable from '../components/AccountingTable';
import AccountingSection from '../components/AccountingSection';
import DatePresetFilter, {
  accountingDateLabels,
} from '../components/DatePresetFilter';
import Alert from '@mui/material/Alert';
import { nonNullable } from 'further-types/utils';

type BalanceSheetRow = {
  id: string;
  label: string;
  periodEnding: number;
  priorYearEnd: number;
};

const pageId = 'balance-sheet';

const BalanceSheet: FC = () => {
  const { isEnabled: showOtherAccounting } = useFlag('other-accounting');
  const { selectedVehicleId, vehicle } = useLayoutContext();

  const { filters, handleChangeFilters } = useFilters<{
    dateOption: AccountingReportDateOption;
    customDate?: CalendarDay;
  }>(pageId, {
    defaultFilters: {
      dateOption: AccountingReportDateOption.EndLastQuarter,
      customDate: undefined,
    },
  });
  const { dateOption, customDate } = filters;
  const isCustomDate = dateOption === AccountingReportDateOption.Custom;
  const isCustomDateWithNoDate = isCustomDate && !customDate;

  const {
    data: balanceSheet,
    isLoading,
    refetch,
  } = api.generalLedger.balanceSheet.useQuery(
    {
      vehicleId: selectedVehicleId ?? '',
      dateOption,
      customDate: isCustomDate && customDate ? customDate : undefined,
    },
    {
      enabled:
        showOtherAccounting && !!selectedVehicleId && !isCustomDateWithNoDate,
    },
  );

  const exportBalanceSheet = api.generalLedger.exportBalanceSheet.useMutation(
    useHandleTrpcMutation<{ file: string; fileName: string }>()({
      refetchType: 'none',
      onSuccess: ({ file }) => {
        const dateLabel =
          balanceSheet?.currentPeriodEnd?.toReadable() ?? 'Unknown date';
        downloadXlxs(
          file,
          `Balance sheet - ${vehicle?.vehicleName} - ${dateLabel}`,
        );
      },
    }),
  );

  if (!showOtherAccounting) {
    return null;
  }

  const handleExport = () => {
    if (isCustomDateWithNoDate) return;
    exportBalanceSheet.mutate({
      vehicleId: selectedVehicleId ?? '',
      dateOption,
      customDate: isCustomDate && customDate ? customDate : undefined,
    });
  };

  if (isLoading || !vehicle) {
    return <PageLoader />;
  }

  const { baseCurrency } = vehicle;

  const formatValue = (value: number) =>
    numberToCurrencyString(value, {
      currency: baseCurrency,
      negativeInBrackets: true,
    });

  const columns: Array<TableColumn<BalanceSheetRow>> = [
    {
      key: 'label',
      label: 'Accounts',
      width: 250,
      textOverflowEllipsis: true,
    },
    {
      key: 'periodEnding',
      label: `Period ending (${
        balanceSheet?.currentPeriodEnd?.toReadable() ?? ''
      })`,
      width: 125,
      render: ({ periodEnding }) => formatValue(periodEnding),
    },
    {
      key: 'priorYearEnd',
      label: `Prior year end (${
        balanceSheet?.priorYearEnd?.toReadable() ?? ''
      })`,
      width: 125,
      render: ({ priorYearEnd }) => formatValue(priorYearEnd),
    },
  ];

  const current = balanceSheet?.current;
  const prior = balanceSheet?.prior;

  const renderRow = (
    id: string,
    label: string,
    periodEnding = 0,
    priorYearEnd = 0,
  ): BalanceSheetRow | undefined =>
    periodEnding !== 0 || priorYearEnd !== 0
      ? { id, label, periodEnding, priorYearEnd }
      : undefined;

  const assetRows = [
    renderRow(
      'investmentsAtCost',
      'Investments at cost',
      current?.investmentsAtCost,
      prior?.investmentsAtCost,
    ),
    renderRow(
      'unrealisedGainLoss',
      'Unrealised gain/loss on investments',
      current?.unrealisedGainLoss,
      prior?.unrealisedGainLoss,
    ),
    renderRow(
      'investmentsAtFairValue',
      'Investments at fair value',
      current?.investmentsAtFairValue,
      prior?.investmentsAtFairValue,
    ),
    renderRow(
      'cashAndCashEquivalents',
      'Cash and cash equivalents',
      current?.cashAndCashEquivalents,
      prior?.cashAndCashEquivalents,
    ),
    renderRow(
      'receivablesAndPrepayments',
      'Receivables and prepayments',
      current?.receivablesAndPrepayments,
      prior?.receivablesAndPrepayments,
    ),
    renderRow(
      'accruedIncome',
      'Accrued income',
      current?.accruedIncome,
      prior?.accruedIncome,
    ),
    renderRow(
      'capitalDueFromPartners',
      'Capital due from partners',
      current?.capitalDueFromPartners,
      prior?.capitalDueFromPartners,
    ),
  ].filter(nonNullable);

  const liabilityRows = [
    renderRow(
      'accountsPayable',
      'Accounts payable',
      current?.accountsPayable,
      prior?.accountsPayable,
    ),
    renderRow(
      'accruedExpenses',
      'Accrued expenses',
      current?.accruedExpenses,
      prior?.accruedExpenses,
    ),
    renderRow(
      'capitalDueToPartners',
      'Capital due to partners',
      current?.capitalDueToPartners,
      prior?.capitalDueToPartners,
    ),
  ].filter(nonNullable);

  const partnerRows = [
    renderRow(
      'generalPartnerCapital',
      'General partner',
      current?.generalPartnerCapital,
      prior?.generalPartnerCapital,
    ),
    renderRow(
      'carryPartnerCapital',
      'Carry partner',
      current?.carryPartnerCapital,
      prior?.carryPartnerCapital,
    ),
    renderRow(
      'limitedPartnersCapital',
      'Limited partners',
      current?.limitedPartnersCapital,
      prior?.limitedPartnersCapital,
    ),
  ].filter(nonNullable);

  return (
    <Column spacing="lg">
      <Row centered spacing="sm" justify="space-between" breakpoint="md">
        <Column spacing="xs">
          <Typography variant="h5" color="grey.900">
            Balance sheet
          </Typography>
          <Typography variant="body2" noWrap>
            {vehicle.vehicleName} as at{' '}
            {isCustomDate
              ? customDate
                ? customDate.toReadable()
                : '...'
              : accountingDateLabels[dateOption].toLowerCase()}
          </Typography>
        </Column>
        <PageFilters
          justify="flex-end"
          align="center"
          actions={[
            {
              label: 'Update',
              variant: 'outlined',
              onClick: refetch,
              disabled: isCustomDateWithNoDate,
            },
            {
              label: 'Export',
              variant: 'contained',
              onClick: handleExport,
              disabled: isCustomDateWithNoDate,
            },
          ]}
        >
          <DatePresetFilter
            dateOption={dateOption}
            customDate={customDate}
            onChange={(dateOption, customDate) =>
              handleChangeFilters({ dateOption, customDate })
            }
          />
        </PageFilters>
      </Row>
      {balanceSheet && !balanceSheet.isBalanced && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Balance sheet does not balance. Net assets (
          {numberToCurrencyString(balanceSheet.current.netAssets, {
            currency: vehicle?.baseCurrency,
          })}
          ) do not equal partners' capital (
          {numberToCurrencyString(balanceSheet.current.totalPartnersCapital, {
            currency: vehicle?.baseCurrency,
          })}
          ). This indicates a general ledger data integrity issue that needs
          investigation.
        </Alert>
      )}
      <SyncScrollProvider>
        <Column>
          <AccountingTable
            columns={columns}
            rows={[]}
            borderless
            displayFooterIfEmptyTable
            externalScroll
          />
          <AccountingSection
            title="Assets"
            titleVariant="h6"
            expandable={false}
            noTopBorder
            noBottomBorder
          >
            <AccountingTable
              columns={columns}
              rows={assetRows}
              isLoading={isLoading}
              borderless
              hideHeader
              externalScroll
              customFooterRow={[
                {
                  key: 'total-assets',
                  backgroundColor: grey[50],
                  content: <Typography variant="h6">Total assets</Typography>,
                },
                {
                  key: 'periodEnding',
                  backgroundColor: grey[50],
                  content: (
                    <Typography variant="h6">
                      {formatValue(balanceSheet?.current.totalAssets ?? 0)}
                    </Typography>
                  ),
                },
                {
                  key: 'priorYearEnd',
                  backgroundColor: grey[50],
                  content: (
                    <Typography variant="h6">
                      {formatValue(balanceSheet?.prior.totalAssets ?? 0)}
                    </Typography>
                  ),
                },
              ]}
            />
          </AccountingSection>
          <AccountingSection
            title="Liabilities"
            titleVariant="h6"
            expandable={false}
            noTopBorder
            noBottomBorder
            customRow={
              <AccountingTable
                columns={columns}
                rows={[]}
                borderless
                hideHeader
                displayFooterIfEmptyTable
                externalScroll
                customFooterRow={[
                  {
                    key: 'net-assets',
                    content: (
                      <Typography variant="h6" fontWeight={700}>
                        Net assets
                      </Typography>
                    ),
                    backgroundColor: grey[100],
                  },
                  {
                    key: 'periodEnding',
                    content: (
                      <Typography variant="h6" fontWeight={700}>
                        {formatValue(balanceSheet?.current.netAssets ?? 0)}
                      </Typography>
                    ),
                    backgroundColor: grey[100],
                  },
                  {
                    key: 'priorYearEnd',
                    content: (
                      <Typography variant="h6" fontWeight={700}>
                        {formatValue(balanceSheet?.prior.netAssets ?? 0)}
                      </Typography>
                    ),
                    backgroundColor: grey[100],
                  },
                ]}
              />
            }
          >
            <AccountingTable
              columns={columns}
              rows={liabilityRows}
              isLoading={isLoading}
              borderless
              hideHeader
              externalScroll
              customFooterRow={[
                {
                  key: 'total-liabilities',
                  backgroundColor: grey[50],
                  content: (
                    <Typography variant="h6">Total liabilities</Typography>
                  ),
                },
                {
                  key: 'periodEnding',
                  backgroundColor: grey[50],
                  content: (
                    <Typography variant="h6">
                      {formatValue(balanceSheet?.current.totalLiabilities ?? 0)}
                    </Typography>
                  ),
                },
                {
                  key: 'priorYearEnd',
                  backgroundColor: grey[50],
                  content: (
                    <Typography variant="h6">
                      {formatValue(balanceSheet?.prior.totalLiabilities ?? 0)}
                    </Typography>
                  ),
                },
              ]}
            />
          </AccountingSection>
          <AccountingSection
            title="Partners' capital"
            titleVariant="h6"
            expandable={false}
            noTopBorder
            noBottomBorder
          >
            <AccountingTable
              columns={columns}
              rows={partnerRows}
              isLoading={isLoading}
              borderless
              hideHeader
              externalScroll
              displayFooterIfEmptyTable
              customFooterRow={[
                {
                  key: 'total-partners',
                  backgroundColor: grey[100],
                  content: (
                    <Typography variant="h6" fontWeight={700}>
                      Total partners' capital
                    </Typography>
                  ),
                },
                {
                  key: 'periodEnding',
                  backgroundColor: grey[100],
                  content: (
                    <Typography variant="h6" fontWeight={700}>
                      {formatValue(
                        balanceSheet?.current.totalPartnersCapital ?? 0,
                      )}
                    </Typography>
                  ),
                },
                {
                  key: 'priorYearEnd',
                  backgroundColor: grey[100],
                  content: (
                    <Typography variant="h6" fontWeight={700}>
                      {formatValue(
                        balanceSheet?.prior.totalPartnersCapital ?? 0,
                      )}
                    </Typography>
                  ),
                },
              ]}
            />
          </AccountingSection>
        </Column>
      </SyncScrollProvider>
    </Column>
  );
};

export default BalanceSheet;
